import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import {
  TOP_UP_STATUSES,
  TopUpStatusSchema,
} from "@polifonia/interlude/billingConstants";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/components/Select";
import { Typography } from "@/components/Typography";
import {
  getMaxDate,
  getMinDate,
  getStatusLabels,
  type TopupFilters,
} from "@/pages/billing/v2/tabs/usageDetails/utils";

const styles = {
  card: {
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  cardContent: {
    padding: "16px 20px",
    "&:last-child": {
      paddingBottom: "16px",
    },
  },
  dateInput: {
    width: 180,
    "& .MuiInputBase-input": {
      padding: "8px 12px",
      fontSize: "0.875rem",
    },
  },
  separator: {
    display: "flex",
    alignItems: "center",
    color: theme.colors.neutral050,
  },
  statusSelect: {
    width: 200,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface TopupFilterBarProps {
  filters: TopupFilters;
  onFiltersChange: (filters: TopupFilters) => void;
}

export const TopupFilterBar: React.FC<TopupFilterBarProps> =
  function TopupFilterBar({ filters, onFiltersChange }) {
    const { t } = useTranslation();

    const minDate = useMemo(() => getMinDate(), []);
    const maxDate = useMemo(() => getMaxDate(), []);

    const statusOptions = useMemo(() => {
      const labels = getStatusLabels(t);
      return TOP_UP_STATUSES.map((status) => ({
        value: status,
        label: labels[status],
      }));
    }, [t]);

    const handleDateFromChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((e) => {
      if (!e.target.value) return;
      onFiltersChange({
        ...filters,
        dateFrom: e.target.value,
      });
    });

    const handleDateToChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((e) => {
      if (!e.target.value) return;
      onFiltersChange({
        ...filters,
        dateTo: e.target.value,
      });
    });

    const handleStatusChange = useHandler(
      (value: string | number | Record<string, unknown>) => {
        const parsed = TopUpStatusSchema.safeParse(value);
        onFiltersChange({
          ...filters,
          status: parsed.success ? parsed.data : null,
        });
      },
    );

    return (
      <Card variant="outlined" sx={styles.card}>
        <CardContent sx={styles.cardContent}>
          <Stack direction="row" alignItems="center" gap="12px">
            <TextField
              type="date"
              size="small"
              placeholder="Date From"
              value={filters.dateFrom}
              onChange={handleDateFromChange}
              sx={styles.dateInput}
              slotProps={{
                htmlInput: {
                  min: minDate,
                  max: filters.dateTo,
                },
              }}
            />
            <Box sx={styles.separator}>
              <Typography variant="body">~</Typography>
            </Box>
            <TextField
              type="date"
              size="small"
              placeholder="Date To"
              value={filters.dateTo}
              onChange={handleDateToChange}
              sx={styles.dateInput}
              slotProps={{
                htmlInput: {
                  min: filters.dateFrom,
                  max: maxDate,
                },
              }}
            />
            <Box sx={styles.statusSelect}>
              <Select
                multiple={false}
                value={filters.status ?? ""}
                onChange={handleStatusChange}
                options={statusOptions}
                placeholder={t("billing.topup.filter.allStatus")}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };
